import React, { useState, useEffect } from 'react';
import { Modal, Tag, Input, Button, Timeline, message } from 'antd';
import ExpressTrackingTimeline from './ExpressTrackingTimeline';
import axios from 'axios';

const { TextArea } = Input;

const STATUS_MAP = {
  'CREATED': '已创建',
  'PRINTED': '已打印',
  'DUPLICATE': '重复订单',
  'SHIPPING': '运输中',
  'PENDING_REVIEW': '未审单',
  'PENDING_CUSTOMS': '待清关',
  'PICKED_UP': '已提货未过机',
  'INSPECTED_PENDING': '已出查验未放行',
  'RELEASED': '已放行',
  'DELIVERING': '派送中',
  'DELIVERED': '已送达',
};

const OrderDetailModal = ({ visible, order, onClose }) => {
  const [notes, setNotes] = useState('');
  const [orderHistory, setOrderHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  const fetchOrderData = async () => {
    if (!order?.id) return;
    try {
      const response = await axios.get(`/api/orders/${order.id}/`);
      setCurrentOrder(response.data);
      setNotes(response.data.notes || '');
    } catch (error) {
      console.error('Error fetching order data:', error);
    }
  };

  useEffect(() => {
    if (order) {
      setCurrentOrder(order);
      setNotes(order.notes || '');
      fetchOrderHistory();
    }
  }, [order]);

  const fetchOrderHistory = async () => {
    if (!order) return;
    try {
      const response = await axios.get(`/api/orders/${order.id}/history/`);
      setOrderHistory(response.data);
    } catch (error) {
      console.error('Error fetching order history:', error);
    }
  };

  const handleSaveNotes = async () => {
    if (!order) return;
    setLoading(true);
    try {
      await axios.patch(`/api/orders/${order.id}/update_notes/`, {
        notes: notes
      });
      message.success('备注已更新');
      fetchOrderHistory();
    } catch (error) {
      console.error('Error updating notes:', error);
      message.error('更新备注失败');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveNotes();
    }
  };

  const getStatusColor = (status) => {
    const label = STATUS_MAP[status] || status;
    switch (label) {
      case '重复订单':
        return 'orange';
      case '运输中':
          return 'processing';
      case '未审单':
        return 'warning';
      case '待清关':
        return 'warning';
      case '已提货未过机':
        return 'processing';
      case '已出查验未放行':
        return 'error';
      case '已放行':
        return 'success';
      case '派送中':
        return 'processing';
      case '已送达':
          return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}年${month}月${day}日 ${hours}:${minutes}`;
  };

  const sectionHeaderStyle = {
    borderBottom: '2px solid #1890ff',
    paddingBottom: '8px',
    marginBottom: '12px'
  };

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px'
  };

  const fieldStyle = {
    flex: '0 0 50%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingRight: '12px',
    lineHeight: '24px'
  };

  const addressFieldStyle = {
    whiteSpace: 'normal',
    marginBottom: '8px'
  };

  return (
    <Modal
      title=""
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1400}
      bodyStyle={{ padding: '12px' }}
    >
      {currentOrder && (
        <div style={{ display: 'flex' }}>
          <div style={{ flex: '0 0 30%', borderRight: '1px solid #f0f0f0', paddingRight: '12px' }}>
            <div style={sectionHeaderStyle}>订单历史记录</div>
            <Timeline>
              {orderHistory
                .filter(history => history.action !== '修改备注')
                .map((history, index) => (
                  <Timeline.Item key={index}>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
                      {formatDate(history.timestamp)}
                    </div>
                    <div>
                      <strong>{history.action}</strong> - {history.user}
                    </div>
                    <div style={{ whiteSpace: 'pre-line', color: '#666' }}>{history.details}</div>
                  </Timeline.Item>
                ))}
            </Timeline>
          </div>

          <div style={{ flex: '0 0 40%', borderRight: '1px solid #f0f0f0', padding: '0 12px' }}>
            <div style={sectionHeaderStyle}>订单详情</div>
            <div style={rowStyle}>
            <div style={fieldStyle}><strong>订单号：</strong> {currentOrder.order_number}</div>
            <div style={fieldStyle}><strong>快递单号：</strong> {currentOrder.express_number}</div>
            </div>

            <div style={rowStyle}>
              <div style={fieldStyle}><strong>状态：</strong> <Tag color={getStatusColor(currentOrder.status)}>{STATUS_MAP[currentOrder.status] || currentOrder.status}</Tag></div>
              <div style={fieldStyle}><strong>批次号：</strong> {currentOrder.batch_number || '未分配'}</div>
            </div>

            <div style={rowStyle}>
              <div style={fieldStyle}><strong>港口：</strong> {currentOrder.port_name || currentOrder.port}</div>
              <div style={fieldStyle}><strong>国内快递：</strong> {currentOrder.courier_name || currentOrder.courier_service}</div>
            </div>

            <div style={rowStyle}>
              <div style={fieldStyle}><strong>收件人：</strong> {currentOrder.label_recipient || currentOrder.recipient_name}</div>
              <div style={fieldStyle}><strong>收件人电话：</strong> {currentOrder.label_phone || currentOrder.recipient_phone}</div>
            </div>

            <div style={addressFieldStyle}>
              <strong>收件地址：</strong> {currentOrder.label_address || `${currentOrder.recipient_province}${currentOrder.recipient_city}${currentOrder.recipient_district}${currentOrder.recipient_address}`}
            </div>

            <div style={rowStyle}>
              <div style={fieldStyle}><strong>寄件人：</strong> {currentOrder.sender_name}</div>
              <div style={fieldStyle}><strong>寄件电话：</strong> {currentOrder.sender_phone}</div>
            </div>

            <div style={addressFieldStyle}>
              <strong>寄件地址：</strong> {currentOrder.sender_address}
            </div>

            <div style={rowStyle}>
              <div style={fieldStyle}><strong>品名：</strong> {currentOrder.product_name}</div>
              <div style={fieldStyle}><strong>单位：</strong> {currentOrder.unit}</div>
            </div>

            <div style={rowStyle}>
              <div style={fieldStyle}><strong>单件重量：</strong> {currentOrder.weight_per_item} KG</div>
              <div style={fieldStyle}><strong>数量：</strong> {currentOrder.quantity}</div>
            </div>

            <div style={rowStyle}>
              <div style={fieldStyle}><strong>创建时间：</strong> {formatDate(currentOrder.created_at)}</div>
              <div style={fieldStyle}><strong>打印次数：</strong> {currentOrder.print_times || 0}</div>
            </div>

            {currentOrder.last_printed_at && (
              <div style={addressFieldStyle}>
                <strong>上次打印时间：</strong> {formatDate(currentOrder.last_printed_at)}
              </div>
            )}

            {(currentOrder.label_recipient || currentOrder.label_phone || currentOrder.label_address) ? null : (
              <>
                {(currentOrder.label_recipient || currentOrder.label_phone) && (
                  <div style={rowStyle}>
                    {currentOrder.label_recipient && (
                      <div style={fieldStyle}><strong>面单收件人：</strong> {currentOrder.label_recipient}</div>
                    )}
                    {currentOrder.label_phone && (
                      <div style={fieldStyle}><strong>面单电话：</strong> {currentOrder.label_phone}</div>
                    )}
                  </div>
                )}

                {currentOrder.label_address && (
                  <div style={addressFieldStyle}>
                    <strong>面单地址：</strong> {currentOrder.label_address}
                  </div>
                )}
              </>
            )}

            {(currentOrder.user_role === 'boss' || currentOrder.user_role === 'manager' || currentOrder.user_role === 'staff') && (
              <div style={rowStyle}>
                <div style={fieldStyle}>
                  <strong>是否删除：</strong>
                  <Tag color={currentOrder.is_deleted ? 'red' : 'green'}>
                    {currentOrder.is_deleted ? '已删除' : '未删除'}
                  </Tag>
                </div>
              </div>
            )}

            <div style={{ marginTop: '16px' }}>
              <div style={{ marginBottom: '8px' }}><strong>备注：</strong></div>
              {orderHistory
                .filter(history => history.action === '修改备注')
                .map((history, index) => (
                  <div key={index} style={{ marginBottom: '12px', fontSize: '12px', color: '#666' }}>
                    <div style={{ marginBottom: '4px' }}>{formatDate(history.timestamp)} - {history.user}</div>
                    <div style={{ whiteSpace: 'pre-line', paddingLeft: '8px' }}>
                      {history.details.includes('改为') ? 
                        history.details.split('改为')[1].replace(/['"]/g, '') : 
                        history.details}
                    </div>
                  </div>
                ))
              }
              <TextArea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onKeyPress={handleKeyPress}
                rows={4}
                style={{ marginBottom: '8px' }}
                placeholder="输入备注内容，按回车或点击保存按钮保存"
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="primary" onClick={handleSaveNotes} loading={loading}>
                  添加备注
                </Button>
              </div>
            </div>
          </div>

          <div style={{ flex: '0 0 30%', paddingLeft: '12px' }}>
            <div style={sectionHeaderStyle}>快递轨迹</div>
            <ExpressTrackingTimeline 
              expressNumber={currentOrder.express_number}
              recipientPhone={currentOrder.recipient_phone?.slice(-4)}
              orderId={currentOrder.id}
              trajectoryText={currentOrder.trajectory_text}
              trajectoryUpdateAt={currentOrder.trajectory_update_at}
              onTrackingUpdate={(updatedData) => {
                if (updatedData?.status === 'DELIVERING' || updatedData?.status === 'DELIVERED') {
                  setCurrentOrder(prev => ({
                    ...prev,
                    status: updatedData.status
                  }));
                }
              }}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default OrderDetailModal;
